import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../../../lib/api";
import { AdminButton, AdminInput, AdminCard } from "../AdminUI";
import { Loader2, Trash2, Upload, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const GalleryTab = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [caption, setCaption] = useState("");
    const fileInput = useRef(null);

    const reload = async () => {
        const r = await api.get("/gallery");
        setImages(r.data);
        setLoading(false);
    };

    useEffect(() => { reload(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post("/gallery", {
                id: undefined,
                url: up.data.url,
                caption,
                sort_order: images.length,
            });
            setCaption("");
            if (fileInput.current) fileInput.current.value = "";
            await reload();
            toast.success("Bild hochgeladen");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Upload fehlgeschlagen");
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Bild löschen?")) return;
        await api.delete(`/gallery/${id}`);
        await reload();
        toast.success("Bild entfernt");
    };

    const updateCaption = async (img, newCaption) => {
        await api.put(`/gallery/${img.id}`, { ...img, caption: newCaption });
        await reload();
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    return (
        <div className="space-y-6 max-w-4xl" data-testid="gallery-tab">
            <AdminCard className="space-y-3">
                <h3 className="font-serif text-xl text-deep-green">Neues Bild hochladen</h3>
                <AdminInput label="Bildunterschrift (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="z.B. Unser Garten im Sommer" data-testid="gallery-caption-input" />
                <input
                    ref={fileInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                    data-testid="gallery-file-input"
                />
                <AdminButton onClick={() => fileInput.current?.click()} loading={uploading} data-testid="gallery-upload-btn">
                    <Upload className="w-4 h-4" /> {uploading ? "Lade hoch..." : "Bild auswählen & hochladen"}
                </AdminButton>
                <p className="text-xs text-deep-green/60">JPG, PNG, WEBP, GIF — max. 10 MB</p>
            </AdminCard>

            <div>
                <h3 className="font-serif text-xl text-deep-green mb-4">Vorhandene Bilder ({images.length})</h3>
                {images.length === 0 ? (
                    <div className="bg-cream-dark/40 rounded-2xl p-10 text-center text-deep-green/60">
                        <ImagePlus className="w-10 h-10 mx-auto mb-2 text-leaf-green" />
                        Noch keine Bilder. Lade dein erstes Bild hoch!
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {images.map((img) => (
                            <div key={img.id} className="bg-white rounded-2xl border border-cream-dark overflow-hidden" data-testid={`gallery-img-${img.id}`}>
                                <img src={fileUrl(img.url)} alt={img.caption || ""} className="w-full aspect-square object-cover" />
                                <div className="p-3 space-y-2">
                                    <input
                                        defaultValue={img.caption}
                                        onBlur={(e) => { if (e.target.value !== img.caption) updateCaption(img, e.target.value); }}
                                        placeholder="Bildunterschrift..."
                                        className="w-full text-sm px-3 py-2 rounded-lg border border-cream-dark focus:outline-none focus:ring-2 focus:ring-garden-green/30"
                                    />
                                    <AdminButton variant="danger" className="w-full" onClick={() => remove(img.id)} data-testid={`gallery-delete-${img.id}`}>
                                        <Trash2 className="w-4 h-4" /> Löschen
                                    </AdminButton>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GalleryTab;
